if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer') && this.submitButton && !this.submitButton.hasAttribute('data-buy-now')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        const submitter = evt.submitter || this.submitButton;
        if (!submitter || submitter.getAttribute('aria-disabled') === 'true') return;

        const buyNow = submitter.hasAttribute('data-buy-now');
        const activeButton = submitter;
        const activeButtonText = activeButton.querySelector('span');
        const activeSpinner = activeButton.querySelector('.loading__spinner');

        this.handleErrorMessage();

        activeButton.setAttribute('aria-disabled', true);
        activeButton.classList.add('loading');
        if (activeSpinner) activeSpinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity'), 10) || 1;

        // Local theme preview (shopify theme dev) cannot open checkout / Shop Pay (HTTP 401).
        // Send Buy Now straight to a live-store cart permalink instead.
        if (buyNow && this.isLocalThemePreview()) {
          window.location = this.getBuyNowPermalink(variantId, quantity);
          return;
        }

        if (this.cart && !buyNow) {
          const sectionIds = this.cart.getSectionsToRender().map((section) => section.id);
          if (!sectionIds.includes('cart-icon-bubble')) {
            sectionIds.push('cart-icon-bubble');
          }
          formData.append('sections', sectionIds.join(','));
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        } else if (!buyNow) {
          formData.append('sections', 'cart-icon-bubble');
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;

        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = activeButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              activeButton.setAttribute('aria-disabled', true);
              if (activeButtonText) activeButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (buyNow) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = this.getCheckoutUrl();
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              this.updateCartIconBubble(response);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);
            this.updateCartIconBubble(response);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            activeButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) activeButton.removeAttribute('aria-disabled');
            if (activeSpinner) activeSpinner.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const buttons = this.querySelectorAll('[type="submit"]');
        buttons.forEach((button) => {
          const buttonText = button.querySelector('span');
          if (disable) {
            button.setAttribute('disabled', 'disabled');
            if (text && buttonText) buttonText.textContent = text;
          } else {
            button.removeAttribute('disabled');
            if (buttonText) {
              buttonText.textContent = button.hasAttribute('data-buy-now')
                ? 'Buy Now'
                : window.variantStrings.addToCart;
            }
          }
        });
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      isLocalThemePreview() {
        const host = window.location.hostname;
        return host === '127.0.0.1' || host === 'localhost';
      }

      getStoreBaseUrl() {
        if (window.shopStoreUrl) return window.shopStoreUrl.replace(/\/$/, '');
        if (window.shopPermanentDomain) return `https://${window.shopPermanentDomain}`;
        if (window.Shopify?.shop) return `https://${window.Shopify.shop}`;
        return '';
      }

      getBuyNowPermalink(variantId, quantity) {
        const base = this.getStoreBaseUrl();
        if (!base || !variantId) return this.getCheckoutUrl();
        return `${base}/cart/${variantId}:${quantity}`;
      }

      getCheckoutUrl() {
        const base = this.getStoreBaseUrl();
        // Prefer the live storefront for checkout so Shop Pay is not loaded via the local proxy.
        if (base && this.isLocalThemePreview()) {
          return `${base}/checkout`;
        }
        return '/checkout';
      }

      updateCartIconBubble(response) {
        const sectionHtml = response?.sections?.['cart-icon-bubble'];
        if (sectionHtml && this.applyCartIconSectionHtml(sectionHtml)) {
          return;
        }
        if (window.refreshHeaderCartIcon) {
          window.refreshHeaderCartIcon();
          return;
        }
        fetch(`${routes.cart_url}.js`)
          .then((res) => res.json())
          .then((cart) => {
            const cartIcon = document.getElementById('cart-icon-bubble');
            if (!cartIcon || !cart) return;
            let bubble = cartIcon.querySelector('.cart-count-bubble');
            if (cart.item_count > 0) {
              if (!bubble) {
                bubble = document.createElement('div');
                bubble.className = 'cart-count-bubble';
                cartIcon.appendChild(bubble);
              }
              bubble.innerHTML =
                cart.item_count < 100
                  ? `<span aria-hidden="true">${cart.item_count}</span>`
                  : '';
            } else {
              bubble?.remove();
            }
          })
          .catch(() => {});
      }

      applyCartIconSectionHtml(sectionHtml) {
        const cartIcon = document.getElementById('cart-icon-bubble');
        if (!cartIcon || !sectionHtml) return false;
        const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const source = doc.querySelector('.shopify-section') || doc.body;
        if (!source) return false;
        cartIcon.innerHTML = source.innerHTML;
        return true;
      }
    }
  );
}
